// Created on 2022-01-12
// Created by Vitaut Tryputsin
//
// Copyright (c) 2021 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
/**
  * Represents undo redo controller.
  */
export class OccUndoRedoController {
    /**
      * Constructor.
      * @param theMaxLength length of states to remember (-1 is unlimited)
      * @param theOnChange callback on change states of undo/redo service
      */
    constructor(theViewer, theMaxLength = 100, theOnChange = null) {
        this.myViewer = theViewer;
        this.myStates = [];
        this.myMaxLength = theMaxLength;
        this.myOnChange = theViewer.eventManager.getEvent("OnChange");
        this.myOnBeforeChange = theViewer.eventManager.getEvent("OnBeforeChange");
        this.myIsBusy = false;
        this.myCurrentState = -1;
    }
    /**
      * Get viewer instance.
      * @returns {OccViewer} viewer instance.
      */
    viewer() { return this.myViewer; }
    /**
      * Reset states.
      */
    reset() {
        this.myStates = [];
        this.myCurrentState = -1;
        this.onChange().emit(0);
    }
    /**
      * Set max length and resets states array.
      * @param theMaxLength  length of states to remember (-1 is unlimited)
      */
    setMaxLength(theMaxLength) {
        this.reset();
        this.myStates = [];
        this.myMaxLength = theMaxLength;
    }
    /**
      * Return on change state observable
      * @return onChange observable
      */
    onChange() { return this.myOnChange; }
    ;
    /**
    * Return on before change state observable
    * @return onBeforeChange observable
    */
    onBeforeChange() { return this.myOnBeforeChange; }
    ;
    /**
      * add state to undo redo service
      * @param theParams new state to add
      */
    add(theParams) {
        if (this.myStates.length != this.myCurrentState + 1) {
            this.myStates.splice(this.myCurrentState + 1);
        }
        this.myStates.push(theParams);
        if (this.myStates.length > this.myMaxLength && this.myMaxLength != -1) {
            this.myStates.shift();
        }
        this.myCurrentState = this.myStates.length - 1;
        this.myOnChange.emit(0);
    }
    /**
      * Check if can undo.
      * @returns can undo status
      */
    canUndo() { return this.myCurrentState > -1; }
    /**
      * Check if can redo.
      * @returns can redo status
      */
    canRedo() { return this.myCurrentState < (this.myStates.length - 1); }
    /**
      * Set busy status (disallow undo/redo if true).
      * @param theBusy the busy status
      */
    setBusy(theBusy) {
        this.myIsBusy = theBusy;
        this.myOnChange.emit(0);
    }
    /**
      * Check busy status.
      * @returns is busy status
      */
    isBusy() { return this.myIsBusy; }
    /**
      * Make redo if can.
      * @returns true if redo happened
      */
    redo() {
        if (this.isBusy() || !this.canRedo()) {
            return false;
        }
        this.myOnBeforeChange.emit(0);
        // extra check because myOnBeforeChange can change undo/redo state.
        if (this.isBusy() || !this.canRedo()) {
            return false;
        }
        const aState = this.myStates[this.myCurrentState + 1];
        aState.onRedo(this, aState);
        this.myCurrentState++;
        this.myOnChange.emit(0);
        return true;
    }
    /**
      * Make und if can.
      * @returns true if undo happened
      */
    undo() {
        if (this.isBusy() || !this.canUndo()) {
            return false;
        }
        this.myOnBeforeChange.emit(1);
        // extra check because myOnBeforeChange can change undo/redo state.
        if (this.isBusy() || !this.canUndo()) {
            return false;
        }
        const aState = this.myStates[this.myCurrentState];
        aState.onUndo(this, aState);
        this.myCurrentState--;
        this.myOnChange.emit(0);
        return true;
    }
}
/**
 * Represents controller which tracks properties changes and store undo/redo state in case when properties changed
 */
export class OccPropertyUndoableController {
    constructor(theUndoRedo) {
        this.myLastSelectedObjects = "";
        this.myLastProperties = [];
        this.myUndoRedo = theUndoRedo;
        this.myViewer = theUndoRedo.viewer();
        this.reset();
        theUndoRedo.onChange().subscribe(this.myViewer.eventManager.createIObservableCallback((theValue) => {
            const aPropsManager = this.myViewer.propertyManager;
            this.myLastSelectedObjects = this.myViewer.selectionManager.selectedObjects;
            aPropsManager.object = this.myLastSelectedObjects;
            this.myLastProperties = aPropsManager.properties;
            return 0;
        }));
        theUndoRedo.onBeforeChange().subscribe(this.myViewer.eventManager.createIObservableCallback((theValue) => {
            this.myViewer.selectionManager.clearSelection(true);
            return 0;
        }));
    }
    /**
     * Reset current data
     */
    reset() {
        this.myLastSelectedObjects = "";
        this.myLastProperties = [];
    }
    /**
     * Check changes for objects if changes has detected then add state to OccUndoRedoController.
     * @param theObjects Objects to check changes (if null - take last selected objects to check)
     */
    checkChanges(theObjects) {
        if (theObjects == null) {
            theObjects = this.myLastSelectedObjects;
        }
        const aPropsManager = this.myViewer.propertyManager;
        // check if nosing selected before - just update private fields
        if (this.myLastSelectedObjects == "") {
            this.myLastSelectedObjects = theObjects;
            aPropsManager.object = theObjects;
            this.myLastProperties = aPropsManager.properties;
            return;
        }
        aPropsManager.object = this.myLastSelectedObjects;
        let aNewProperties = aPropsManager.properties;
        // check if something changed
        let aStatus = this._isEqualProperties(this.myLastProperties, aNewProperties);
        const anUndoRedo = this.myUndoRedo;
        // status == 0 means that something changed
        if (aStatus == 0) {
            anUndoRedo.add({
                name: "properties change",
                onUndo: (context, theParams) => {
                    aPropsManager.object = theParams.dataBefore[0];
                    aPropsManager.properties = theParams.dataBefore[1];
                    aPropsManager.update();
                },
                onRedo: (context, theParams) => {
                    aPropsManager.object = theParams.dataAfter[0];
                    aPropsManager.properties = theParams.dataAfter[1];
                    aPropsManager.update();
                },
                dataBefore: [this.myLastSelectedObjects, this.myLastProperties.slice()],
                dataAfter: [this.myLastSelectedObjects, aNewProperties.slice()],
            });
        }
        // if arrays is different for same input
        else if (aStatus == -1) {
            anUndoRedo.reset();
            throw new Error("Error: Something wrong with properties: "
                + JSON.stringify(this.myLastProperties) + JSON.stringify(aNewProperties));
        }
        // store last state
        aPropsManager.object = theObjects;
        this.myLastSelectedObjects = theObjects;
        this.myLastProperties = aPropsManager.properties;
    }
    /**
     * Compare two arrays of properties
     * @param {ObjectProperties[]} theProps1 first array of properties
     * @param {ObjectProperties[]} theProps2 second array of properties
     * @returns equality state ('-1' - different arrays) ('0' - has changes) ('1' - no changes).
     */
    _isEqualProperties(theProps1, theProps2) {
        if (theProps1.length != theProps2.length) {
            return -1;
        }
        for (let i in theProps1) {
            let aProp1 = theProps1[i];
            let aProp2 = theProps2[i];
            if (aProp1.object != aProp2.object) {
                return -1;
            }
            if (aProp1.position.x != aProp2.position.x || aProp1.position.y != aProp2.position.y
                || aProp1.position.z != aProp2.position.z ||
                aProp1.rotation.x != aProp2.rotation.x || aProp1.rotation.y != aProp2.rotation.y
                || aProp1.rotation.z != aProp2.rotation.z || aProp1.rotation.w != aProp2.rotation.w ||
                aProp1.scale != aProp2.scale || aProp1.hasCustomMaterial != aProp2.hasCustomMaterial
                || aProp1.color != aProp2.color ||
                aProp1.metallic != aProp2.metallic || aProp1.roughness != aProp2.roughness
                || aProp1.displayMode != aProp2.displayMode || aProp1.displayed != aProp2.displayed) {
                return 0;
            }
        }
        return 1;
    }
}
